import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useLayoutEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import TutorialOverlay from './ui/TutorialOverlay'
import TutorialControls from './ui/TutorialControls'
import TutorialCharacter from './ui/TutorialCharacter'
import TutorialTopLayout from './ui/TutorialTopLayout'
import MiniTips2 from './miniTips/MiniTips2'
import { getDefinition, findStepIndex, getStep } from './TutorialEngine'
import type { TutorialId, TutorialRuntimeState, TutorialStep } from './types'
import { useAuth } from '../contexts/AuthContext'
import { loadRemoteStep, saveRemoteStep, clearRemoteTutorial } from './persistence'

interface TutorialContextValue {
  state: TutorialRuntimeState
  start: (id: TutorialId, stepId?: string | null) => void
  resume: (id?: TutorialId) => void
  next: () => void
  prev: () => void
  skip: () => void
  finish: () => void
  goTo: (index: number) => void
}

const TutorialContext = createContext<TutorialContextValue | undefined>(undefined)

// eslint-disable-next-line react-refresh/only-export-components
export function useTutorial(): TutorialContextValue {
  const ctx = useContext(TutorialContext)
  if (!ctx) throw new Error('useTutorial deve ser usado dentro de <TutorialProvider>')
  return ctx
}

const DEFAULT_ID: TutorialId = 'first_run'

const buildProgressKey = (tutorialId: TutorialId, version: number, uid?: string | null) => (
  uid && uid.length > 0
    ? `tutorial:${uid}:${tutorialId}:v${version}`
    : `tutorial:${tutorialId}:v${version}`
)

// Namespace para flags auxiliares do tutorial por usuário
const buildFlagKey = (flag: string, uid?: string | null) => (
  uid && uid.length > 0 ? `tutorial:${uid}:${flag}` : `tutorial:${flag}`
)

const TutorialProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()

  const [runtime, setRuntime] = useState<TutorialRuntimeState>({
    active: false,
    tutorialId: null,
    version: 1,
    stepIndex: -1,
  })

  const currentDef = useMemo(() => {
    return runtime.tutorialId ? getDefinition(runtime.tutorialId) : getDefinition(DEFAULT_ID)
  }, [runtime.tutorialId])

  const currentStep = useMemo(() => getStep(currentDef, Math.max(0, runtime.stepIndex)), [currentDef, runtime.stepIndex])

  // Área alvo (anchor)
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null)
  const observerRef = useRef<MutationObserver | null>(null)
  const waitHandledRef = useRef<string | null>(null)
  const [canNext, setCanNext] = useState(true)
  const [canSkip, setCanSkip] = useState(true)
  const scrolledStepRef = useRef<string | null>(null)
  // Recentrar caso algum foco/auto-layout desloque o alvo logo após o scroll inicial
  const recenterTimerRef = useRef<number | null>(null)
  const recenterTriesRef = useRef<number>(0)
  const prevFocusRef = useRef<HTMLElement | null>(null)
  const rafRef = useRef<number | null>(null)
  const resizeTimerRef = useRef<number | null>(null)
  const prevRectRef = useRef<DOMRect | null>(null)
  const ioRef = useRef<IntersectionObserver | null>(null)
  const roRef = useRef<ResizeObserver | null>(null)
  const routeGuardRef = useRef<string | null>(null)
  const stepIdRef = useRef<string | null>(null)
  // Flag de retomada (refresh/reopen): usada para aplicar regras especiais no fluxo 6.x
  const resumedRef = useRef(false)
  // Guard para ignorar bootstrap via deep-link quando acabamos de pular
  const skipGuardRef = useRef(false)
  // Modo livre: desliga overlay e restrições para permitir ações fora do spotlight (ex.: adicionar mais membros no 4.6)
  const [freeMode, setFreeMode] = useState(false)
  // Timeout para liberar Skip em hard gate sem âncora (offline/elemento não disponível)
  const hardGateTimeoutRef = useRef<number | null>(null)
  const [anchorMissing, setAnchorMissing] = useState(false)
  // Debounce para gravação remota
  const remoteSaveTimerRef = useRef<number | null>(null)
  
  // Grace Period: delay inicial para evitar que o tutorial apareça muito rápido (V1.5)
  const gracePeriodTimerRef = useRef<number | null>(null)
  const [isInGracePeriod, setIsInGracePeriod] = useState(false)
  // Target Ready Detection: contador de tentativas de encontrar âncora (V1.5)
  const targetReadyAttemptsRef = useRef<number>(0)
  const targetReadyTimerRef = useRef<number | null>(null)

  // Bootstrap: limpeza leve de chaves legadas globais (não-namespaced)
  useEffect(() => {
    try {
      const toRemove: string[] = []
      // Chaves globais legadas que podem interferir entre usuários
      ;['tutorial:suppressed', 'tutorial:lastMemberId', 'tutorial:in_med_flow', 'tutorial:med_incomplete'].forEach((k) => {
        try { if (localStorage.getItem(k) != null) toRemove.push(k) } catch { /* ignore */ }
      })
      // Progresso legado sem namespace: tutorial:<tutorialId>:v<number>
      try {
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i) || ''
          if (!k.startsWith('tutorial:')) continue
          const parts = k.split(':')
          // Apenas 3 partes: [tutorial, <tutorialId>, vN] → legado (não-namespaced)
          if (parts.length === 3 && /^v\d+$/i.test(parts[2])) {
            toRemove.push(k)
          }
        }
      } catch { /* ignore */ }

      // Executar remoção fora do loop de iteração
      if (toRemove.length > 0) {
        toRemove.forEach((k) => { try { localStorage.removeItem(k) } catch { /* ignore */ } })
        try {
          Sentry.addBreadcrumb({
            category: 'tutorial',
            message: 'legacy_keys_cleaned',
            data: { removed: toRemove },
            level: 'info'
          })
        } catch { /* ignore */ }
      }
    } catch { /* ignore */ }
    // uma vez no mount
  }, [])

  const updateTargetRect = useCallback(() => {
    // Ignorar atualizações disparadas por observers de passos anteriores
    const createdForId = currentStep?.id || null
    if (stepIdRef.current !== createdForId) {
      return
    }
    // Se o passo espera uma rota e ela já foi atingida, não destacar nenhum anchor
    if (currentStep?.waitFor && (currentStep.waitFor as any).type === 'route') {
      const p = (currentStep.waitFor as any).path as string | undefined
      if (p && location.pathname === p) {
        setTargetRect(null)
        return
      }
    }
    if (!currentStep?.anchor) {
      setTargetRect(null)
      return
    }
    const el = document.querySelector(`[data-tutorial-anchor="${currentStep.anchor}"]`) as HTMLElement | null
    if (!el) {
      // Target Ready Detection: tentar novamente com delay progressivo (V1.5)
      if (targetReadyAttemptsRef.current < 5) {
        const delay = 100 * (targetReadyAttemptsRef.current + 1) // 100ms, 200ms, 300ms, 400ms, 500ms
        targetReadyAttemptsRef.current += 1
        
        if (targetReadyTimerRef.current) {
          window.clearTimeout(targetReadyTimerRef.current)
        }
        
        targetReadyTimerRef.current = window.setTimeout(() => {
          targetReadyTimerRef.current = null
          scheduleUpdateTargetRect()
        }, delay) as unknown as number
      }
      setTargetRect(null)
      return
    }
    
    // Elemento encontrado: resetar contador de tentativas
    targetReadyAttemptsRef.current = 0
    if (targetReadyTimerRef.current) {
      window.clearTimeout(targetReadyTimerRef.current)
      targetReadyTimerRef.current = null
    }
    // Auto-scroll: garantir que o alvo esteja visível (especialmente em modais com rolagem)
    if (scrolledStepRef.current !== currentStep.id) {
      // Sempre centralizar uma vez por passo ( UX desejada em 6.2–6.15 dentro do modal )
      let blockPosition: ScrollLogicalPosition = 'center'
      if ((currentStep as any).uiPosition === 'top') {
        blockPosition = 'start'
      } else if ((currentStep as any).uiPosition === 'center') {
        blockPosition = 'center'
      }
      const sid = currentStep.id || ''
      const isStep6 = sid.includes('first_run.step_06_')
      const a = currentStep.anchor || ''
      const isMedAnchor = isStep6 && (a.startsWith('med-') || a === 'cycle-summary' || a === 'btn-save-medication')
      try {
        if (isMedAnchor) {
          // Centralizar no container do modal e medir imediatamente após o scroll
          centerInContainer(el, blockPosition === 'start' ? 'start' : 'center')
          const r2 = el.getBoundingClientRect()
          prevRectRef.current = r2
          setTargetRect(r2)
          scrolledStepRef.current = currentStep.id
          return
        } else {
          el.scrollIntoView({ block: blockPosition, inline: 'nearest', behavior: 'smooth' })
        }
      } catch { void 0; }
      scrolledStepRef.current = currentStep.id
    }
    const rect = el.getBoundingClientRect()
    const prev = prevRectRef.current
    const epsilon = 0.5
    const changed =
      !prev ||
      Math.abs(prev.top - rect.top) > epsilon ||
      Math.abs(prev.left - rect.left) > epsilon ||
      Math.abs(prev.width - rect.width) > epsilon ||
      Math.abs(prev.height - rect.height) > epsilon
    if (changed) {
      prevRectRef.current = rect
      setTargetRect(rect)
    }

    // Fallback: se o elemento ficou "grudado" no topo ou muito embaixo logo após o scroll inicial,
    // tentar recentralizar mais uma vez (limite de 2 tentativas por passo) para sustentar a UX do modal 6.x
    try {
      const vh = (window.innerHeight || document.documentElement.clientHeight)
      const nearTop = rect.top < 140
      const nearBottom = rect.bottom > (vh - 140)
      if (scrolledStepRef.current === currentStep.id && (nearTop || nearBottom) && recenterTriesRef.current < 2) {
        if (recenterTimerRef.current) {
          window.clearTimeout(recenterTimerRef.current)
          recenterTimerRef.current = null
        }
        const sid = currentStep.id || ''
        const isStep6 = sid.includes('first_run.step_06_')
        const a = currentStep.anchor || ''
        const isMedAnchor = isStep6 && (a.startsWith('med-') || a === 'cycle-summary' || a === 'btn-save-medication')
        const delay = isMedAnchor ? 0 : 80
        recenterTimerRef.current = window.setTimeout(() => {
          try {
            if (isMedAnchor) {
              centerInContainer(el, 'center')
            } else {
              el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' })
            }
          } catch { /* ignore */ }
          recenterTriesRef.current += 1
        }, delay) as unknown as number
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep?.anchor, currentStep?.waitFor, currentStep?.id, location.pathname])

  // Performance: agendar atualização via requestAnimationFrame
  const scheduleUpdateTargetRect = useCallback(() => {
    if (rafRef.current != null) return
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null
      updateTargetRect()
    })
     
  }, [updateTargetRect])

  // Focar sem alterar o scroll (evita que o navegador reposicione o modal para o topo)
  const focusNoScroll = (el: HTMLElement | null) => {
    if (!el) return
    try {
      ;(el as any).focus({ preventScroll: true })
    } catch {
      try { el.focus() } catch { /* ignore */ }
    }
  }

  // Encontra o container scrollável mais próximo (overflow-y: auto|scroll)
  const getScrollContainer = (el: HTMLElement | null): HTMLElement | null => {
    if (!el) return null
    let node: HTMLElement | null = el.parentElement
    while (node && node !== document.body) {
      try {
        const style = window.getComputedStyle(node)
        const oy = style.overflowY
        if ((oy === 'auto' || oy === 'scroll') && node.scrollHeight > node.clientHeight) {
          return node
        }
      } catch { /* ignore */ }
      node = node.parentElement
    }
    return null
  }

  // Centraliza o elemento no container, compensando cabeçalho sticky (se houver)
  const centerInContainer = (el: HTMLElement, position: 'center' | 'start' = 'center') => {
    const container = getScrollContainer(el)
    if (!container) {
      // Fallback: viewport
      try { el.scrollIntoView({ block: position, inline: 'nearest', behavior: 'auto' }) } catch { /* ignore */ }
      return
    }
    // Calcular offset acumulado do elemento em relação ao container (mais estável)
    const getOffsetTopInContainer = (target: HTMLElement, root: HTMLElement): number => {
      let y = 0
      let n: HTMLElement | null = target
      while (n && n !== root) {
        y += n.offsetTop
        n = n.offsetParent as HTMLElement | null
      }
      return y
    }
    let stickyTop = 0
    try {
      // Heurística: primeiro filho sticky no topo do container
      const kids = Array.from(container.children) as HTMLElement[]
      const sticky = kids.find((k) => window.getComputedStyle(k).position === 'sticky')
      if (sticky) {
        // Considerar altura declarada do sticky; se não houver, usar clientHeight
        stickyTop = sticky.clientHeight || sticky.getBoundingClientRect().height
      }
    } catch { /* ignore */ }
    // Reservar altura do balão da UI no topo da viewport, se presente
    try {
      const ui = document.querySelector('[data-tutorial-ui="1"]') as HTMLElement | null
      if (ui) {
        const r = ui.getBoundingClientRect()
        // Se o balão está no topo da viewport, reservar sua altura + margem
        if (r.top <= 60) {
          const reserve = Math.min(container.clientHeight * 0.5, r.height + 16)
          stickyTop += reserve
        }
      }
    } catch { /* ignore */ }
    const visibleH = Math.max(0, container.clientHeight - stickyTop)
    const offsetTop = getOffsetTopInContainer(el, container)
    let targetTop: number
    if (position === 'center') {
      const centerOffset = stickyTop + visibleH / 2
      targetTop = offsetTop - (centerOffset - el.clientHeight / 2)
    } else {
      targetTop = offsetTop - stickyTop - 24 // margem sob header
    }
    try {
      const finalTop = Math.max(0, targetTop)
      ;(container as any).scrollTop = finalTop
      // Forçar layout para garantir que getBoundingClientRect reflita a posição pós-scroll
      void (container as any).offsetHeight
    } catch { /* ignore */ }
  }

  // Inferência de rota esperada para o passo atual
  const inferExpectedPath = useCallback((step: TutorialStep | null): string | null => {
    if (!step) return null
    // 1) Se o passo explicitamente espera uma rota, usar essa rota
    if (step.waitFor && (step.waitFor as any).type === 'route') {
      const p = (step.waitFor as any).path as string | undefined
      if (p) return p
    }
    // 2) Mapear pelas âncoras conhecidas
    const map: Record<string, string> = {
      // setup família
      'input-family-name': '/setup-family',
      'btn-continue-create-family': '/setup-family',
      // add member
      'member-name-input': '/add-member',
      'member-birthdate-input': '/add-member',
      'member-gender-select': '/add-member',
      'member-avatar-combined': '/add-member',
      'member-avatar-picker': '/add-member',
      'btn-add-member': '/add-member',
      'btn-continue-add-members': '/add-member',
      // settings
      'nav-settings': '/settings',
      'btn-enable-push': '/settings',
      // dashboard/home
      'dashboard-summary': '/dashboard',
      'btn-open-curiosities': '/dashboard',
      'btn-fact-mark-read': '/dashboard',
      'btn-add-treatment': '/dashboard',
      // member page (dinâmica): usar prefixo '/member' e NÃO navegar forçadamente
      'member-menu-ellipsis': '/member',
      'menu-new-medication': '/member',
      'med-name': '/member',
      'med-potency-number': '/member',
      'med-potency-type': '/member',
      'med-dosage-type': '/member',
      'med-dosage-quantity': '/member',
      'med-start-date': '/member',
      'med-frequency': '/member',
      'med-schedule': '/member',
      'med-usage-days': '/member',
      'med-pause-days': '/member',
      'med-total-period': '/member',
      'cycle-summary': '/member',
      'btn-save-medication': '/member',
      'nav-home': '/dashboard',
      'btn-bell-alerts': '/dashboard',
      'alerts-panel': '/dashboard',
    }
    if (step.anchor && map[step.anchor]) return map[step.anchor]
    // 3) Fallback: prefixos do id
    const id = step.id || ''
    // Específico: 7.1 (introdução aos avisos) deve permanecer no dashboard do membro
    if (id === 'first_run.step_07_01_push_intro') return '/member'
    if (id.includes('first_run.step_03_')) return '/setup-family'
    if (id.includes('first_run.step_04_')) return '/add-member'
    if (id.includes('first_run.step_05_')) return '/dashboard'
    if (id.includes('first_run.step_06_')) return '/member'
    if (id.includes('first_run.step_07_02') || id.includes('first_run.step_07_03') || id.includes('first_run.step_07_04')) return '/settings'
    if (id.includes('first_run.step_07_')) return '/dashboard'
    return null
  }, [])

  useEffect(() => {
    if (!runtime.active) return
    updateTargetRect()
    const onResize = () => {
      if (resizeTimerRef.current) window.clearTimeout(resizeTimerRef.current)
      resizeTimerRef.current = window.setTimeout(() => {
        scheduleUpdateTargetRect()
      }, 120) as unknown as number
    }
    const onScroll = () => scheduleUpdateTargetRect()
    window.addEventListener('resize', onResize)
    window.addEventListener('scroll', onScroll, true)
    
    // ✅ iOS: Listeners do visualViewport para recalcular spotlight quando teclado virtual abre/fecha
    const onVisualViewportChange = () => scheduleUpdateTargetRect()
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', onVisualViewportChange)
      window.visualViewport.addEventListener('scroll', onVisualViewportChange)
    }

    // Observar alterações no DOM: dar preferência ao container do anchor, fallback para body
    const sel = currentStep?.anchor ? `[data-tutorial-anchor="${currentStep.anchor}"]` : null
    const anchorEl = sel ? (document.querySelector(sel) as HTMLElement | null) : null
    const obsTarget = anchorEl?.parentElement || document.body
    const obs = new MutationObserver(() => scheduleUpdateTargetRect())
    obs.observe(obsTarget, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'data-tutorial-anchor'] })
    observerRef.current = obs

    // IntersectionObserver para o anchor (reage a visibilidade)
    if (anchorEl) {
      const io = new IntersectionObserver((entries) => {
        const entry = entries[0]
        if (!entry) return
        // Sempre reagir, mas evitar setState redundante via diff de retângulo
        scheduleUpdateTargetRect()
      }, { root: null, threshold: [0, 0.25, 0.5, 0.75, 1] })
      io.observe(anchorEl)
      ioRef.current = io

      // ResizeObserver: reage a mudanças de tamanho/layout do anchor
      try {
        const ro = new ResizeObserver(() => scheduleUpdateTargetRect())
        ro.observe(anchorEl)
        roRef.current = ro
      } catch { void 0; }

      // Eventos de transição/animação do anchor (header do dashboard tem animações na entrada)
      const onTransitionEnd = () => scheduleUpdateTargetRect()
      const onAnimationEnd = () => scheduleUpdateTargetRect()
      try {
        anchorEl.addEventListener('transitionend', onTransitionEnd)
        anchorEl.addEventListener('animationend', onAnimationEnd)
      } catch { void 0; }

      // Re-medições extra após montar o step para capturar estados pós-animação
      try { window.setTimeout(() => scheduleUpdateTargetRect(), 120) } catch { void 0; }
      try { window.setTimeout(() => scheduleUpdateTargetRect(), 320) } catch { void 0; }
      try { window.setTimeout(() => scheduleUpdateTargetRect(), 650) } catch { void 0; }
    }

    return () => {
      window.removeEventListener('resize', onResize)
      window.removeEventListener('scroll', onScroll, true)
      
      // ✅ iOS: Remover listeners do visualViewport
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', onVisualViewportChange)
        window.visualViewport.removeEventListener('scroll', onVisualViewportChange)
      }
      
      if (resizeTimerRef.current) {
        window.clearTimeout(resizeTimerRef.current)
        resizeTimerRef.current = null
      }
      if (rafRef.current != null) {
        window.cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      obs.disconnect()
      if (ioRef.current) {
        ioRef.current.disconnect()
        ioRef.current = null
      }
      if (roRef.current) {
        try { roRef.current.disconnect() } catch { void 0; }
        roRef.current = null
      }
      // Remover listeners de transição/animação se presentes
      try {
        const sel = currentStep?.anchor ? `[data-tutorial-anchor="${currentStep.anchor}"]` : null
        const anchorEl = sel ? (document.querySelector(sel) as HTMLElement | null) : null
        if (anchorEl) {
          anchorEl.removeEventListener('transitionend', scheduleUpdateTargetRect as any)
          anchorEl.removeEventListener('animationend', scheduleUpdateTargetRect as any)
        }
      } catch { void 0; }
      observerRef.current = null
    }
  }, [runtime.active, updateTargetRect, currentStep?.anchor, scheduleUpdateTargetRect])

  // Reset auto-scroll flag ao trocar de passo
  useEffect(() => {
    scrolledStepRef.current = null
    // limpar tentativa de recentralização
    if (recenterTimerRef.current) {
      try { window.clearTimeout(recenterTimerRef.current) } catch { /* ignore */ }
      recenterTimerRef.current = null
    }
    recenterTriesRef.current = 0
  }, [runtime.stepIndex])

  // Atualiza a referência do step atual o mais cedo possível para bloquear callbacks antigos
  useLayoutEffect(() => {
    stepIdRef.current = currentStep?.id || null
  }, [currentStep?.id])

  // Ao trocar de step, descartar retângulo anterior para evitar flash do alvo passado
  useEffect(() => {
    prevRectRef.current = null
    setTargetRect(null)
    // Resetar contador de tentativas de Target Ready Detection
    targetReadyAttemptsRef.current = 0
  }, [currentStep?.id])
  
  // Grace Period: delay após mudança de rota para evitar flash do tutorial (V1.5)
  useEffect(() => {
    if (!runtime.active) return
    
    // Limpar timers existentes
    if (gracePeriodTimerRef.current) {
      window.clearTimeout(gracePeriodTimerRef.current)
      gracePeriodTimerRef.current = null
    }
    
    // Ativar grace period por 600ms após mudança de rota
    setIsInGracePeriod(true)
    gracePeriodTimerRef.current = window.setTimeout(() => {
      setIsInGracePeriod(false)
      gracePeriodTimerRef.current = null
    }, 600) as unknown as number
    
    return () => {
      if (gracePeriodTimerRef.current) {
        window.clearTimeout(gracePeriodTimerRef.current)
        gracePeriodTimerRef.current = null
      }
    }
  }, [location.pathname, runtime.active])

  // Removido bloqueio de rolagem do body: manter scroll habilitado

  // Iniciar via query string (?tutorial=first_run&step=NN) priorizando estado remoto (Supabase)
  useEffect(() => {
    if (runtime.active) return
    const params = new URLSearchParams(location.search)
    const qTutorial = params.get('tutorial') as TutorialId | null
    const qStep = params.get('step')
    if (!qTutorial) return
    // Se acabamos de acionar Pular e limpamos a URL, ignore um possível estado de corrida
    if (skipGuardRef.current) return
    // Se o usuário escolheu Pular anteriormente, não rebootstrapar automaticamente (namespaced)
    try {
      const supKey = buildFlagKey('suppressed', user?.id)
      if (localStorage.getItem(supKey) === '1') return
    } catch { /* ignore */ }
    ;(async () => {
      const def = getDefinition(qTutorial)
      const userId = user?.id ?? null
      const clamp = (idx: number) => Math.max(0, Math.min(idx, def.steps.length - 1))
      const onSetupRoutes = location.pathname.startsWith('/setup-family') || location.pathname.startsWith('/add-member')
      let resumeSource: 'url' | 'remote' | 'local' | 'legacy' | 'route' | 'default' = 'default'
      if (onSetupRoutes) {
        try {
          localStorage.removeItem(buildFlagKey('lastMemberId', userId))
          localStorage.removeItem(buildFlagKey('in_med_flow', userId))
          localStorage.removeItem(buildFlagKey('med_incomplete', userId))
        } catch { /* ignore */ }
      }

      let startIndex: number | null = null
      let authoritative = false

      if (qStep) {
        const parsed = Number(qStep)
        const isNaNStep = Number.isNaN(parsed)
        if (isNaNStep) {
          try { Sentry.addBreadcrumb({ category: 'tutorial', message: 'tutorial_step_parse_invalid', data: { step: qStep }, level: 'info' }) } catch { /* ignore */ }
        }
        startIndex = clamp(isNaNStep ? 0 : parsed - 1)
        resumeSource = 'url'
        authoritative = true
      } else {
        if (userId) {
          try {
            const remote = await loadRemoteStep(userId, qTutorial)
            if (remote != null) {
              startIndex = clamp(remote)
              authoritative = true
              resumeSource = 'remote'
            }
          } catch { /* ignore */ }
        }

        if (startIndex == null) {
          try {
            const raw = localStorage.getItem(buildProgressKey(qTutorial, def.version, userId))
            if (raw) {
              const saved = Number(JSON.parse(raw)?.stepIndex)
              if (!Number.isNaN(saved)) startIndex = clamp(saved)
              if (startIndex != null) resumeSource = 'local'
            }
          } catch { /* ignore localStorage parse error */ }
        }

        if (startIndex == null) {
          // Compatibilidade com chaves antigas (sem namespace)
          try {
            const legacy = localStorage.getItem(`tutorial:${qTutorial}:v${def.version}`)
            if (legacy) {
              const saved = Number(JSON.parse(legacy)?.stepIndex)
              if (!Number.isNaN(saved)) startIndex = clamp(saved)
              if (startIndex != null) resumeSource = 'legacy'
            }
          } catch { /* ignore */ }
        }

        if (startIndex == null) {
          if (location.pathname.startsWith('/setup-family')) {
            startIndex = findStepIndex(def, 'first_run.step_02_route_setup_family')
            resumeSource = 'route'
          } else if (location.pathname.startsWith('/add-member')) {
            startIndex = findStepIndex(def, 'first_run.step_04_01_member_name')
            resumeSource = 'route'
          }
        }
      }

      let resolvedIndex = startIndex != null ? startIndex : 0

      try {
        const inFlow = localStorage.getItem(buildFlagKey('in_med_flow', userId)) === '1'
        const medIncomplete = localStorage.getItem(buildFlagKey('med_incomplete', userId)) === '1'
        const last = localStorage.getItem(buildFlagKey('lastMemberId', userId))
        const startStepId = getStep(def, resolvedIndex)?.id || ''
        const isStep6 = startStepId.includes('first_run.step_06_')
        if (!authoritative && (medIncomplete || inFlow || (isStep6 && startStepId !== 'first_run.step_06_01_open_member_menu')) && !!last && !onSetupRoutes) {
          const openIdx = findStepIndex(def, 'first_run.step_06_01_open_member_menu')
          if (openIdx >= 0) resolvedIndex = openIdx
        }
      } catch { /* ignore */ }

      setRuntime({ active: true, tutorialId: qTutorial, version: def.version, stepIndex: resolvedIndex })
      // Atualiza imediatamente a URL (?step=)
      try {
        const params2 = new URLSearchParams(location.search)
        params2.set('tutorial', qTutorial)
        params2.set('step', String(resolvedIndex + 1))
        const next = params2.toString()
        const curr = (location.search || '').replace(/^\?/, '')
        if (next !== curr) {
          navigate({ pathname: location.pathname, search: next }, { replace: true })
        }
      } catch { /* ignore */ }
      resumedRef.current = true
      try { Sentry.addBreadcrumb({ category: 'tutorial', message: 'tutorial_started', data: { tutorial_id: qTutorial, version: def.version, step: resolvedIndex + 1, source: resumeSource }, level: 'info' }) } catch { /* ignore */ }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, runtime.active, location.search, location.pathname])

  // Breadcrumb a cada step
  useEffect(() => {
    if (!runtime.active) return
    const step = getStep(currentDef, runtime.stepIndex)
    if (!step) return
    Sentry.addBreadcrumb({ category: 'tutorial', message: 'tutorial_step_view', data: { tutorial_id: runtime.tutorialId, version: runtime.version, step_index: runtime.stepIndex, step_id: step.id }, level: 'info' })
    // Atualiza flag de fluxo de medicação para retomada
    try {
      const id = step.id || ''
      if (id.includes('first_run.step_06_') && id !== 'first_run.step_06_16_first_badge') {
        localStorage.setItem(buildFlagKey('in_med_flow', user?.id), '1')
      } else if (id.includes('first_run.step_07_') || id === 'first_run.step_06_16_first_badge') {
        localStorage.removeItem(buildFlagKey('in_med_flow', user?.id))
      }
      // Em passos iniciais (1.x/2.x/3.x) limpar chaves herdadas para evitar interferência em contas novas
      if (id.includes('first_run.step_01_') || id.includes('first_run.step_02_') || id.includes('first_run.step_03_')) {
        localStorage.removeItem(buildFlagKey('lastMemberId', user?.id))
        localStorage.removeItem(buildFlagKey('in_med_flow', user?.id))
        localStorage.removeItem(buildFlagKey('med_incomplete', user?.id))
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runtime.active, runtime.stepIndex, runtime.tutorialId, runtime.version, currentDef])

  // Persistência local simples do progresso para permitir resume()
  const saveProgress = useCallback((uid: string | null | undefined, id: TutorialId, version: number, stepIndex: number) => {
    try {
      localStorage.setItem(buildProgressKey(id, version, uid), JSON.stringify({ stepIndex }))
      localStorage.removeItem(`tutorial:${id}:v${version}`)
    } catch { void 0; }
  }, [])

  useEffect(() => {
    if (!runtime.active) return
    const id = (runtime.tutorialId || DEFAULT_ID) as TutorialId
    saveProgress(user?.id, id, runtime.version, runtime.stepIndex)
  }, [runtime.active, runtime.tutorialId, runtime.version, runtime.stepIndex, saveProgress, user?.id])

  // Persistência B: salvar remoto (Supabase) em cada mudança de step — com debounce
  useEffect(() => {
    if (!runtime.active) return
    const uid = user?.id
    if (!uid) return
    const id = (runtime.tutorialId || DEFAULT_ID) as TutorialId
    if (remoteSaveTimerRef.current) {
      window.clearTimeout(remoteSaveTimerRef.current)
      remoteSaveTimerRef.current = null
    }
    remoteSaveTimerRef.current = window.setTimeout(() => {
      ;(async () => {
        try {
          const ok = await saveRemoteStep(uid, id, runtime.version, runtime.stepIndex)
          if (ok) {
            try {
              Sentry.addBreadcrumb({
                category: 'tutorial',
                message: 'tutorial_remote_saved',
                data: { tutorial_id: id, version: runtime.version, step_index: runtime.stepIndex },
                level: 'info'
              })
            } catch { /* ignore */ }
          }
        } catch { /* ignore */ }
      })()
    }, 400) as unknown as number
    return () => {
      if (remoteSaveTimerRef.current) {
        window.clearTimeout(remoteSaveTimerRef.current)
        remoteSaveTimerRef.current = null
      }
    }
  }, [runtime.active, runtime.tutorialId, runtime.version, runtime.stepIndex, user?.id])

  // waitFor: controla avanço automático e habilitação de botões
  useEffect(() => {
    if (!runtime.active) return
    const step = currentStep
    // Reset controles padrão
    setCanNext(true)
    setCanSkip(true)
    setAnchorMissing(false)

    if (!step) return

    // Em hard gate com waitFor, desabilitar Próximo/Pular
    const hasWait = !!step.waitFor
    const isHard = step.gate === 'hard'
    if (isHard && hasWait) {
      setCanNext(false)
      // Mantém o botão Pular sempre ativo
    }

    // Limpar listeners anteriores
    let cleanup: (() => void) | null = null

    const triggerNextOnce = () => {
      if (waitHandledRef.current === step.id) return
      waitHandledRef.current = step.id
      // avançar para o próximo step sem depender de 'next' (evita ordem de declaração)
      setRuntime((s) => {
        const last = currentDef.steps.length - 1
        const to = Math.min(s.stepIndex + 1, last)
        return { ...s, stepIndex: to }
      })
    }

    if (step.waitFor) {
      switch (step.waitFor.type) {
        case 'route': {
          // Se já está na rota, avançar
          if (location.pathname === step.waitFor.path) {
            triggerNextOnce()
          }
          // Reagir a mudanças de rota via efeito dependente de location.pathname
          cleanup = null
          break
        }
        case 'click': {
          const sel = `[data-tutorial-anchor="${step.waitFor.anchor}"]`
          let el = document.querySelector(sel) as HTMLElement | null
          const onClick = () => triggerNextOnce()
          if (el) {
            el.addEventListener('click', onClick, { capture: true })
          }
          const mo = new MutationObserver(() => {
            if (!el) {
              el = document.querySelector(sel) as HTMLElement | null
              if (el) {
                el.addEventListener('click', onClick, { capture: true })
              }
            }
          })
          mo.observe(document.body, { childList: true, subtree: true })
          cleanup = () => {
            if (el) el.removeEventListener('click', onClick, { capture: true } as any)
            mo.disconnect()
          }
          break
        }
        case 'delay': {
          const id = window.setTimeout(() => triggerNextOnce(), step.waitFor.ms)
          cleanup = () => window.clearTimeout(id)
          break
        }
        case 'event': {
          const ev = step.waitFor as any
          const handler = () => {
            if (step.waitAction === 'unlock') {
              if (waitHandledRef.current === step.id) return
              waitHandledRef.current = step.id
              // Em modo unlock: liberar Próximo e manter Pular ativo
              setCanNext(true)
            } else {
              triggerNextOnce()
            }
          }
          window.addEventListener(ev.name as string, handler as any)
          cleanup = () => window.removeEventListener(ev.name as string, handler as any)
          break
        }
        default:
          break
      }
    } else {
      // Sem waitFor: permitir navegação normal
      setCanNext(true)
      setCanSkip(true)
    }

    return () => {
      if (cleanup) cleanup()
      // Ao trocar de step, permitir que o próximo step dispare novamente
      waitHandledRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runtime.active, currentStep, location.pathname])

  // Listener especial para passo 6.17: auto-advance quando badge fechar (Opção 2: dual gate)
  useEffect(() => {
    if (!runtime.active || !currentStep) return
    if (currentStep.id !== 'first_run.step_06_17_badge_explanation') return

    let handled = false
    const handler = () => {
      // Proteção contra múltiplas execuções
      if (handled) return
      handled = true
      
      // Avançar automaticamente quando o badge for fechado
      setRuntime((s) => {
        // Verificar novamente se ainda estamos no passo 6.17
        if (s.stepIndex !== currentDef.steps.findIndex(st => st.id === 'first_run.step_06_17_badge_explanation')) {
          return s
        }
        const last = currentDef.steps.length - 1
        if (s.stepIndex >= last) return s
        return { ...s, stepIndex: s.stepIndex + 1 }
      })
    }

    window.addEventListener('tutorial:badge:first-medication:closed', handler)
    return () => {
      window.removeEventListener('tutorial:badge:first-medication:closed', handler)
      handled = false
    }
  }, [runtime.active, currentStep, currentDef.steps])

  // Timeout para liberar Skip em hard gate quando âncora não aparece (offline/elemento indisponível)
  useEffect(() => {
    if (!runtime.active || !currentStep) return
    
    // Limpar timeout anterior
    if (hardGateTimeoutRef.current) {
      window.clearTimeout(hardGateTimeoutRef.current)
      hardGateTimeoutRef.current = null
    }
    
    const isHard = currentStep.gate === 'hard'
    const hasAnchor = !!currentStep.anchor
    
    // Só monitorar se for hard gate com âncora esperada
    if (!isHard || !hasAnchor) return
    
    // Aguardar 1500ms: se targetRect continuar null, liberar Skip e marcar âncora como ausente
    hardGateTimeoutRef.current = window.setTimeout(() => {
      if (targetRect === null) {
        setCanSkip(true)
        setAnchorMissing(true)
        try {
          const step = currentStep
          Sentry.addBreadcrumb({
            category: 'tutorial',
            message: 'anchor_missing_timeout',
            data: { tutorial_id: runtime.tutorialId, step_id: step?.id, anchor: step?.anchor },
            level: 'warning'
          })
        } catch { /* ignore */ }
        console.warn(`[Tutorial] Âncora "${currentStep.anchor}" não encontrada após 1500ms (hard gate). Skip liberado.`)
      }
    }, 1500) as unknown as number
    
    return () => {
      if (hardGateTimeoutRef.current) {
        window.clearTimeout(hardGateTimeoutRef.current)
        hardGateTimeoutRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runtime.active, currentStep, targetRect])

  const start = useCallback((id: TutorialId, stepId?: string | null) => {
    const def = getDefinition(id)
    const idx = findStepIndex(def, stepId ?? null)
    // Reabrindo manualmente: limpar supressão (namespaced + legado)
    try {
      localStorage.removeItem(buildFlagKey('suppressed', user?.id))
      localStorage.removeItem('tutorial:suppressed')
    } catch { /* ignore */ }
    setRuntime({ active: true, tutorialId: id, version: def.version, stepIndex: idx })
    // Limpar resíduos perigosos ao iniciar em fluxo inicial ou em rotas de setup
    try {
      const s = getStep(def, idx)
      const sid = s?.id || ''
      const onSetupRoutes = location.pathname.startsWith('/setup-family') || location.pathname.startsWith('/add-member')
      if (onSetupRoutes || sid.includes('first_run.step_01_') || sid.includes('first_run.step_02_') || sid.includes('first_run.step_03_')) {
        localStorage.removeItem(buildFlagKey('lastMemberId', user?.id))
        localStorage.removeItem(buildFlagKey('in_med_flow', user?.id))
        localStorage.removeItem(buildFlagKey('med_incomplete', user?.id))
      }
    } catch { /* ignore */ }
    try {
      localStorage.setItem(buildProgressKey(id, def.version, user?.id), JSON.stringify({ stepIndex: idx }))
      localStorage.removeItem(`tutorial:${id}:v${def.version}`)
    } catch { void 0; }
    try { Sentry.addBreadcrumb({ category: 'tutorial', message: 'tutorial_started', data: { tutorial_id: id, version: def.version, step: idx + 1, source: 'manual' }, level: 'info' }) } catch { /* ignore */ }
  }, [user?.id, location.pathname])

  const resume = useCallback(async (id?: TutorialId) => {
    const tutorialId = id ?? DEFAULT_ID
    const def = getDefinition(tutorialId)
    const userId = user?.id ?? null
    const clamp = (idxNum: number) => Math.max(0, Math.min(idxNum, def.steps.length - 1))
    let idx: number | null = null
    let authoritative = false
    let resumeSource: 'remote' | 'local' | 'legacy' | 'route' | 'default' = 'default'
    // Reabrindo manualmente: limpar supressão (namespaced + legado)
    try {
      localStorage.removeItem(buildFlagKey('suppressed', user?.id))
      localStorage.removeItem('tutorial:suppressed')
    } catch { /* ignore */ }

    if (userId) {
      try {
        const remote = await loadRemoteStep(userId, tutorialId)
        if (remote != null) {
          idx = clamp(remote)
          authoritative = true
          resumeSource = 'remote'
        }
      } catch { /* ignore */ }
    }

    if (idx == null) {
      try {
        const raw = localStorage.getItem(buildProgressKey(tutorialId, def.version, userId))
        if (raw) {
          const saved = Number(JSON.parse(raw)?.stepIndex)
          if (!Number.isNaN(saved)) idx = clamp(saved)
          if (idx != null) resumeSource = 'local'
        }
      } catch { /* ignore */ }
    }

    if (idx == null) {
      try {
        const legacy = localStorage.getItem(`tutorial:${tutorialId}:v${def.version}`)
        if (legacy) {
          const saved = Number(JSON.parse(legacy)?.stepIndex)
          if (!Number.isNaN(saved)) idx = clamp(saved)
          if (idx != null) resumeSource = 'legacy'
        }
      } catch { /* ignore */ }
    }

    if (idx == null) {
      if (location.pathname.startsWith('/setup-family')) {
        idx = findStepIndex(def, 'first_run.step_02_route_setup_family')
        resumeSource = 'route'
      } else if (location.pathname.startsWith('/add-member')) {
        idx = findStepIndex(def, 'first_run.step_04_01_member_name')
        resumeSource = 'route'
      } else {
        idx = 0
      }
    }

    let resolvedIdx = idx ?? 0

    try {
      const inFlow = localStorage.getItem(buildFlagKey('in_med_flow', userId)) === '1'
      const medIncomplete = localStorage.getItem(buildFlagKey('med_incomplete', userId)) === '1'
      const last = localStorage.getItem(buildFlagKey('lastMemberId', userId))
      const onSetupRoutes = location.pathname.startsWith('/setup-family') || location.pathname.startsWith('/add-member')
      const idAt = getStep(def, resolvedIdx)?.id || ''
      const isStep6 = idAt.includes('first_run.step_06_')
      if (!authoritative && (medIncomplete || inFlow || (isStep6 && idAt !== 'first_run.step_06_01_open_member_menu')) && !!last && !onSetupRoutes) {
        const openIdx = findStepIndex(def, 'first_run.step_06_01_open_member_menu')
        if (openIdx >= 0) resolvedIdx = openIdx
      }
    } catch { /* ignore */ }

    resumedRef.current = true
    setRuntime({ active: true, tutorialId, version: def.version, stepIndex: resolvedIdx })
    try {
      const params2 = new URLSearchParams(location.search)
      params2.set('tutorial', tutorialId)
      params2.set('step', String(resolvedIdx + 1))
      const next = params2.toString()
      const curr = (location.search || '').replace(/^\?/, '')
      if (next !== curr) {
        navigate({ pathname: location.pathname, search: next }, { replace: true })
      }
    } catch { /* ignore */ }
    try { Sentry.addBreadcrumb({ category: 'tutorial', message: 'tutorial_resumed', data: { tutorial_id: tutorialId, version: def.version, step: resolvedIdx + 1, source: resumeSource }, level: 'info' }) } catch { /* ignore */ }
  }, [user?.id, location.pathname, location.search, navigate])

  // Flush do salvamento remoto ao perder visibilidade/fechar a página
  useEffect(() => {
    if (!runtime.active || !user?.id) return
    const flush = () => {
      try {
        if (remoteSaveTimerRef.current) {
          window.clearTimeout(remoteSaveTimerRef.current)
          remoteSaveTimerRef.current = null
        }
        const id = (runtime.tutorialId || DEFAULT_ID) as TutorialId
        void (async () => {
          try {
            const ok = await saveRemoteStep(user.id!, id, runtime.version, runtime.stepIndex)
            if (ok) {
              try { Sentry.addBreadcrumb({ category: 'tutorial', message: 'tutorial_remote_flush', data: { tutorial_id: id, version: runtime.version, step_index: runtime.stepIndex }, level: 'info' }) } catch { /* ignore */ }
            }
          } catch { /* ignore */ }
        })()
      } catch { /* ignore */ }
    }
    const onVis = () => {
      try { if (document.visibilityState === 'hidden') flush() } catch { /* ignore */ }
    }
    window.addEventListener('visibilitychange', onVis, true)
    window.addEventListener('pagehide', flush, true)
    return () => {
      window.removeEventListener('visibilitychange', onVis, true)
      window.removeEventListener('pagehide', flush, true)
    }
  }, [runtime.active, runtime.tutorialId, runtime.version, runtime.stepIndex, user?.id])

  const goTo = useCallback((index: number) => {
    setRuntime((s) => ({ ...s, stepIndex: Math.max(0, Math.min(index, currentDef.steps.length - 1)) }))
  }, [currentDef.steps.length])

  const next = useCallback(() => {
    setRuntime((s) => {
      const last = currentDef.steps.length - 1
      if (s.stepIndex >= last) {
        Sentry.addBreadcrumb({ category: 'tutorial', message: 'tutorial_completed', data: { tutorial_id: s.tutorialId, version: s.version }, level: 'info' })
        return { ...s, active: false, stepIndex: -1 }
      }
      const to = s.stepIndex + 1
      return { ...s, stepIndex: to }
    })
  }, [currentDef.steps.length])

  const prev = useCallback(() => {
    setRuntime((s) => ({ ...s, stepIndex: Math.max(0, s.stepIndex - 1) }))
  }, [])

  const skip = useCallback(() => {
    // Capturar contexto atual para limpeza
    const id = (runtime.tutorialId || DEFAULT_ID) as TutorialId
    const version = runtime.version
    // Armar guard para evitar bootstrap pelo efeito de deep-link
    skipGuardRef.current = true
    // Marcar supressão para impedir rebootstrap automático até reabrir manualmente
    try { localStorage.setItem(buildFlagKey('suppressed', user?.id), '1') } catch { /* ignore */ }
    // Primeiro, limpar parâmetros de URL (para não rebootstrapar) sem acionar roteador
    try {
      const url = new URL(window.location.href)
      url.searchParams.delete('tutorial')
      url.searchParams.delete('step')
      window.history.replaceState(null, '', url.toString())
    } catch { /* ignore */ }
    // Agora encerrar o tutorial no runtime
    setRuntime((s) => {
      Sentry.addBreadcrumb({ category: 'tutorial', message: 'tutorial_skipped', data: { tutorial_id: s.tutorialId, version: s.version, from_step: s.stepIndex + 1 }, level: 'info' })
      return { ...s, active: false, stepIndex: -1 }
    })
    // Limpar progresso local e remoto para evitar retomada indesejada
    try {
      localStorage.removeItem(buildProgressKey(id, version, user?.id))
      localStorage.removeItem(`tutorial:${id}:v${version}`)
    } catch { /* ignore */ }
    if (user?.id) { void clearRemoteTutorial(user.id, id) }
    try { localStorage.setItem('push_cta:just_ended_at', String(Date.now())) } catch { /* ignore */ }
    // Fallback: recarregar a página para garantir desmontagem caso alguma extensão quebre o estado de navegação
    try { window.setTimeout(() => window.location.reload(), 0) } catch { /* ignore */ }
    // Desarmar o guard após um pequeno intervalo
    window.setTimeout(() => { skipGuardRef.current = false }, 200)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runtime.tutorialId, runtime.version, location.search, location.pathname, user?.id])

  const finish = useCallback(() => {
    // Capturar contexto atual
    const id = (runtime.tutorialId || DEFAULT_ID) as TutorialId
    const version = runtime.version

    // Guard para evitar bootstrap pelo efeito de deep-link durante o fechamento
    skipGuardRef.current = true
    // Marcar supressão para impedir rebootstrap automático até reabrir manualmente
    try { localStorage.setItem(buildFlagKey('suppressed', user?.id), '1') } catch { /* ignore */ }

    // Limpar parâmetros de URL (para não rebootstrapar) sem acionar roteador
    try {
      const url = new URL(window.location.href)
      url.searchParams.delete('tutorial')
      url.searchParams.delete('step')
      window.history.replaceState(null, '', url.toString())
    } catch { /* ignore */ }

    // Encerrar o tutorial no runtime
    setRuntime((s) => {
      Sentry.addBreadcrumb({ category: 'tutorial', message: 'tutorial_completed', data: { tutorial_id: s.tutorialId, version: s.version }, level: 'info' })
      return { ...s, active: false, stepIndex: -1 }
    })

    // Limpar progresso local e flags auxiliares
    try {
      localStorage.removeItem(buildProgressKey(id, version, user?.id))
      localStorage.removeItem(`tutorial:${id}:v${version}`)
      localStorage.removeItem(buildFlagKey('lastMemberId', user?.id))
      localStorage.removeItem(buildFlagKey('in_med_flow', user?.id))
      localStorage.removeItem(buildFlagKey('med_incomplete', user?.id))
    } catch { /* ignore */ }

    // Limpar progresso remoto
    if (user?.id) { void clearRemoteTutorial(user.id, id) }

    // Resetar MiniTips neste dispositivo ao concluir o tutorial
    try {
      const keysToRemove: string[] = []
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i) || ''
        if (k.startsWith('minitip:')) keysToRemove.push(k)
      }
      keysToRemove.forEach((k) => localStorage.removeItem(k))
      Sentry.addBreadcrumb({ category: 'minitip', message: 'reset_all_on_tutorial_finish', data: { removed: keysToRemove.length }, level: 'info' })
    } catch { void 0; }

    try { localStorage.setItem('push_cta:just_ended_at', String(Date.now())) } catch { /* ignore */ }

    // Fallback: recarregar a página para garantir desmontagem caso alguma extensão quebre o estado de navegação
    try { window.setTimeout(() => window.location.reload(), 0) } catch { /* ignore */ }
    // Desarmar o guard após um pequeno intervalo
    window.setTimeout(() => { skipGuardRef.current = false }, 200)
  }, [runtime.tutorialId, runtime.version, user?.id])

  // Atualizar query param ao trocar step (para deep-linking/report)
  useEffect(() => {
    if (!runtime.active) return
    const params = new URLSearchParams(location.search)
    const stepNum = runtime.stepIndex + 1
    params.set('tutorial', runtime.tutorialId || DEFAULT_ID)
    params.set('step', String(stepNum))
    const next = params.toString()
    const curr = (location.search || '').replace(/^\?/, '')
    if (next !== curr) {
      navigate({ pathname: location.pathname, search: next }, { replace: true })
    }
  }, [runtime.active, runtime.stepIndex, runtime.tutorialId, location.pathname, location.search, navigate])

  // Expor estado no DOM para que componentes externos possam ajustar comportamento
  useEffect(() => {
    try {
      if (runtime.active) {
        const step = getStep(currentDef, runtime.stepIndex)
        document.body.dataset.tutorialActive = '1'
        document.body.dataset.tutorialId = (runtime.tutorialId || DEFAULT_ID) as string
        if (step?.id) document.body.dataset.tutorialStepId = step.id
      } else {
        delete document.body.dataset.tutorialActive
        delete document.body.dataset.tutorialId
        delete document.body.dataset.tutorialStepId
      }
    } catch { void 0; }
  }, [runtime.active, runtime.tutorialId, runtime.stepIndex, currentDef])

  const value = useMemo<TutorialContextValue>(() => ({ state: runtime, start, resume, next, prev, skip, finish, goTo }), [runtime, start, resume, next, prev, skip, finish, goTo])

  // Visible: tutorial ativo + passo válido + fora do grace period (V1.5)
  const visible = runtime.active && !!currentStep && !isInGracePeriod
  const isFirst = runtime.stepIndex <= 0
  const isLast = runtime.stepIndex >= currentDef.steps.length - 1
  const hideUi = freeMode && currentStep?.id === 'first_run.step_04_06_continue_or_more'

  // Garantir rota esperada ao trocar de step ou retomar
  useEffect(() => {
    if (!runtime.active) return
    const step = getStep(currentDef, runtime.stepIndex)
    // Passos com âncoras de navegação devem aguardar o clique do usuário (hard gate),
    // não devemos forçar navegação automática.
    if (step?.anchor === 'nav-settings' || step?.anchor === 'nav-home') {
      return
    }
    // Em retomada de medicação, não forçar navegação aqui; o efeito de retomada cuidará disso
    try {
      const medIncomplete = ((localStorage.getItem(buildFlagKey('med_incomplete', user?.id)) ?? localStorage.getItem('tutorial:med_incomplete')) === '1')
      const inMedFlow = ((localStorage.getItem(buildFlagKey('in_med_flow', user?.id)) ?? localStorage.getItem('tutorial:in_med_flow')) === '1')
      const id = step?.id || ''
      const isMedContext = id.includes('first_run.step_05_') || id.includes('first_run.step_06_')
      if (resumedRef.current && (medIncomplete || inMedFlow) && isMedContext) {
        return
      }
    } catch { /* ignore */ }
    const expected = inferExpectedPath(step)
    if (!expected) return
    // Não sair das telas de setup; só navegue se for para a própria tela de setup
    const onSetupRoutes = location.pathname.startsWith('/setup-family') || location.pathname.startsWith('/add-member')
    if (onSetupRoutes && !(expected === '/setup-family' || expected === '/add-member')) {
      return
    }
    // Exceção: garantir fundo como dashboard do membro em 6.16 (first badge) e 7.1 (push intro)
    try {
      const id = step?.id || ''
      const needsMemberBg = id === 'first_run.step_06_16_first_badge' || id === 'first_run.step_07_01_push_intro'
      if (expected === '/member' && needsMemberBg && !location.pathname.startsWith('/member')) {
        const last = (localStorage.getItem(buildFlagKey('lastMemberId', user?.id)) || localStorage.getItem('tutorial:lastMemberId'))
        if (last) {
          const params = new URLSearchParams(location.search)
          const stepNum = runtime.stepIndex + 1
          params.set('tutorial', runtime.tutorialId || DEFAULT_ID)
          params.set('step', String(stepNum))
          routeGuardRef.current = id
          navigate({ pathname: `/member/${last}`, search: params.toString() }, { replace: true })
          return
        }
      }
    } catch { /* ignore */ }
    const matches = expected === '/member' ? location.pathname.startsWith('/member') : location.pathname === expected
    if (!matches) {
      const sid = step?.id || ''
      if (routeGuardRef.current === sid) return
      routeGuardRef.current = sid
      const params = new URLSearchParams(location.search)
      const stepNum = runtime.stepIndex + 1
      params.set('tutorial', runtime.tutorialId || DEFAULT_ID)
      params.set('step', String(stepNum))
      // Não forçar navegação para '/member' (rota dinâmica). Deixar a UI levar ao membro correto.
      if (expected !== '/member') {
        navigate({ pathname: expected, search: params.toString() }, { replace: true })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runtime.active, runtime.stepIndex, runtime.tutorialId, currentDef, location.pathname, location.search, navigate, inferExpectedPath])

  // Retomada inteligente para passos 6.x (medicação):
  // - Se estivermos retomando (refresh/reopen) em passo 6.x diferente de 6.1, OU vindo de 5.5 com fluxo de medicação incompleto, resetar para 6.1 (menu do membro)
  // - Garantir que estamos na rota '/member/{last}' (usando tutorial:lastMemberId)
  // - Em fluxo normal (não retomada), manter comportamento atual (abrir modal para anchors 'med-*')
  useEffect(() => {
    if (!runtime.active) return
    const step = getStep(currentDef, runtime.stepIndex)
    if (!step) return
    const id = step.id || ''
    const anchor = step.anchor || ''
    // Só atuar aqui em 6.x ou 5.5; demais passos não devem acionar lógica de medicação
    const isStep6 = id.includes('first_run.step_06_')
    const isAt55 = id === 'first_run.step_05_05_add_treatment'
    if (!isStep6 && !isAt55) return
    // Não interferir quando usuário está nas telas de setup da família ou add-member
    if (location.pathname.startsWith('/setup-family') || location.pathname.startsWith('/add-member')) return

    // Detectar flags de retomada do fluxo de medicação
    let medIncomplete = false
    let inMedFlow = false
    try {
      medIncomplete = localStorage.getItem('tutorial:med_incomplete') === '1'
      inMedFlow = localStorage.getItem('tutorial:in_med_flow') === '1'
    } catch { /* ignore */ }

    // 1) Em retomada: regras para reset e navegação na jornada de medicação
    //    - Reset para 6.1 somente se estivermos em 6.x (exceto 6.1) OU se estivermos em 5.5 com fluxo incompleto
    const shouldResetTo61 = resumedRef.current && ((isStep6 && id !== 'first_run.step_06_01_open_member_menu') || (isAt55 && (medIncomplete || inMedFlow)))
    let openIdx = -1
    if (shouldResetTo61) {
      try {
        const openId = 'first_run.step_06_01_open_member_menu'
        openIdx = findStepIndex(currentDef, openId)
        if (openIdx >= 0) {
          setRuntime((s) => ({ ...s, stepIndex: openIdx }))
          // recalc spotlight após reset de step
          window.setTimeout(() => scheduleUpdateTargetRect(), 60)
        }
      } catch { /* ignore */ }
    }

    // 2) Em retomada: navegar para o último membro se não estivermos em '/member'
    const shouldGoToMember = resumedRef.current && isStep6 && !location.pathname.startsWith('/member')
    if (shouldGoToMember) {
      void (async () => {
        try {
          let last = (localStorage.getItem(buildFlagKey('lastMemberId', user?.id)) || localStorage.getItem('tutorial:lastMemberId'))
          if (!last) {
            // Fallback: descobrir o primeiro membro disponível e persistir
            try {
              const { default: FamilyService } = await import('../services/familyService')
              const familyService = FamilyService.getInstance()
              const members = await familyService.getUserFamilyMembers()
              if (Array.isArray(members) && members.length > 0 && members[0]?.id) {
                last = members[0].id
                try { localStorage.setItem(buildFlagKey('lastMemberId', user?.id), last) } catch { /* ignore */ }
              }
            } catch { /* ignore */ }
          }
          if (last) {
            const params = new URLSearchParams(location.search)
            const stepNum = (openIdx >= 0 ? openIdx : runtime.stepIndex) + 1
            params.set('tutorial', runtime.tutorialId || DEFAULT_ID)
            params.set('step', String(stepNum))
            navigate({ pathname: `/member/${last}`, search: params.toString() }, { replace: true })
            try { window.setTimeout(() => scheduleUpdateTargetRect(), 100) } catch { /* ignore */ }
          }
        } catch { /* ignore */ }
      })()
    }

    // 3) Após aplicar as ações de retomada, desarmar a flag para evitar loops
    if (resumedRef.current && (shouldResetTo61 || shouldGoToMember)) {
      resumedRef.current = false
      return
    }

    // 3) Em fluxo normal: garantir que o modal de medicação esteja aberto para anchors de campos
    const medAnchors = new Set([
      'med-name',
      'med-potency-number',
      'med-potency-type',
      'med-dosage-type',
      'med-dosage-quantity',
      'med-start-date',
      'med-frequency',
      'med-schedule',
      'med-usage-days',
      'med-pause-days',
      'med-total-period',
      'cycle-summary',
      'btn-save-medication',
    ])
    if (medAnchors.has(anchor) && !resumedRef.current) {
      try {
        // Pequeno delay para garantir montagem do MemberDashboard
        window.setTimeout(() => {
          try {
            window.dispatchEvent(new CustomEvent('tutorial:ensure-medication-modal-open'))
            // Atualiza spotlight após o modal abrir
            scheduleUpdateTargetRect()
          } catch { /* ignore */ }
        }, 16)
      } catch { /* ignore */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runtime.active, runtime.stepIndex, runtime.tutorialId, currentDef, location.pathname, location.search, navigate])

  // Focus management: focar anchor ao entrar no passo e restaurar apenas quando o passo mudar
  useEffect(() => {
    if (!visible || freeMode) return
    const getAllowedFocusables = (): HTMLElement[] => {
      const nodes: HTMLElement[] = []
      if (currentStep?.anchor) {
        const el = document.querySelector(`[data-tutorial-anchor="${currentStep.anchor}"]`) as HTMLElement | null
        if (el) {
          if (el.tabIndex < 0) try { el.tabIndex = 0 } catch { void 0; }
          nodes.push(el)
        }
      }
      document
        .querySelectorAll(
          '[data-tutorial-ui="1"] button, [data-tutorial-ui="1"] [href], [data-tutorial-ui="1"] input, [data-tutorial-ui="1"] select, [data-tutorial-ui="1"] textarea, [data-tutorial-ui="1"] [tabindex]:not([tabindex="-1"])'
        )
        .forEach((n) => nodes.push(n as HTMLElement))
      const filtered = nodes.filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null)
      return Array.from(new Set(filtered))
    }

    prevFocusRef.current = (document.activeElement as HTMLElement) || null
    const allowed = getAllowedFocusables()
    if (allowed.length > 0) {
      try { focusNoScroll(allowed[0]) } catch { void 0; }
    }

    return () => {
      const prev = prevFocusRef.current
      if (prev) {
        try { focusNoScroll(prev) } catch { void 0; }
        prevFocusRef.current = null
      }
    }
  }, [visible, currentStep?.anchor, runtime.stepIndex, freeMode])

  // Keydown handlers (não mexe em foco; evita blur ao habilitar Próximo)
  useEffect(() => {
    if (!visible || freeMode) return
    const getAllowedFocusables = (): HTMLElement[] => {
      const nodes: HTMLElement[] = []
      if (currentStep?.anchor) {
        const el = document.querySelector(`[data-tutorial-anchor="${currentStep.anchor}"]`) as HTMLElement | null
        if (el) {
          if (el.tabIndex < 0) try { el.tabIndex = 0 } catch { void 0; }
          nodes.push(el)
        }
      }
      document
        .querySelectorAll(
          '[data-tutorial-ui="1"] button, [data-tutorial-ui="1"] [href], [data-tutorial-ui="1"] input, [data-tutorial-ui="1"] select, [data-tutorial-ui="1"] textarea, [data-tutorial-ui="1"] [tabindex]:not([tabindex="-1"])'
        )
        .forEach((n) => nodes.push(n as HTMLElement))
      const filtered = nodes.filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null)
      return Array.from(new Set(filtered))
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        const list = getAllowedFocusables()
        if (list.length === 0) return
        const active = document.activeElement as HTMLElement | null
        let idx = Math.max(0, list.findIndex((el) => el === active))
        if (e.shiftKey) {
          idx = (idx - 1 + list.length) % list.length
        } else {
          idx = (idx + 1) % list.length
        }
        e.preventDefault()
        try { focusNoScroll(list[idx]) } catch { void 0; }
        return
      }

      const isLastStep = runtime.stepIndex >= currentDef.steps.length - 1
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        const active = document.activeElement as HTMLElement | null
        const isEditable = (() => {
          if (!active) return false
          const tag = active.tagName
          if (tag === 'INPUT' || tag === 'TEXTAREA') return true
          if ((active as HTMLElement).isContentEditable) return true
          const role = active.getAttribute('role')
          if (role === 'textbox' || role === 'combobox') return true
          return false
        })()
        if (!isEditable) {
          e.preventDefault()
          if (e.key === 'ArrowRight') {
            if (!isLastStep && canNext) next()
          } else {
            prev()
          }
        }
        return
      }
      if (e.key === 'Enter') {
        const active = document.activeElement as HTMLElement | null
        const isEditable = (() => {
          if (!active) return false
          const tag = active.tagName
          if (tag === 'INPUT' || tag === 'TEXTAREA') return true
          if ((active as HTMLElement).isContentEditable) return true
          const role = active.getAttribute('role')
          if (role === 'textbox' || role === 'combobox') return true
          return false
        })()
        if (!isEditable) {
          e.preventDefault()
          if (isLastStep) {
            finish()
          } else if (canNext) {
            next()
          }
        }
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        if (canSkip) {
          const ok = window.confirm('Deseja pular o tutorial? Você poderá reabrir nas Configurações.')
          if (ok) skip()
        }
        return
      }
    }

    window.addEventListener('keydown', onKeyDown, true)
    return () => {
      window.removeEventListener('keydown', onKeyDown, true)
    }
  }, [visible, canNext, canSkip, runtime.stepIndex, currentDef.steps.length, next, prev, skip, finish, currentStep?.anchor, freeMode])

  // Ao trocar de passo, sair do modo livre
  useEffect(() => {
    setFreeMode(false)
  }, [currentStep?.id])

  return (
    <TutorialContext.Provider value={value}>
      {children}
      {visible && (
        <>
          <TutorialOverlay visible={currentStep?.overlay !== false && !freeMode} targetRect={targetRect} restrictOutsideClicks={currentStep?.gate === 'hard'} />
          {!hideUi && (currentStep?.uiPosition === 'top' ? (
            <TutorialTopLayout
              key={`top-layout-${currentStep?.id}`}
              text={currentStep?.text || ''}
              onPrev={prev}
              onNext={next}
              onSkip={skip}
              onFinish={finish}
              isFirst={isFirst}
              isLast={isLast}
              canNext={canNext}
              canSkip={canSkip}
              anchorMissing={anchorMissing}
              pose={currentStep?.characterPose}
            />
          ) : (
            <>
              <TutorialCharacter 
                key={`character-${currentStep?.id}`}
                text={currentStep?.text || ''} 
                uiPosition={currentStep?.uiPosition} 
                pose={currentStep?.characterPose}
                videoSrc={currentStep?.characterVideo}
                layoutOverride={currentStep?.layoutOverride}
                enableStaggeredAnimation={currentStep?.enableStaggeredAnimation}
              />
              <TutorialControls
                key={`controls-${currentStep?.id}`}
                onPrev={prev}
                onNext={next}
                onSkip={skip}
                onFinish={finish}
                isFirst={isFirst}
                isLast={isLast}
                canNext={canNext}
                canSkip={canSkip}
                uiPosition={currentStep?.uiPosition}
                layoutOverride={currentStep?.layoutOverride?.controls}
                enableStaggeredAnimation={currentStep?.enableStaggeredAnimation}
                extraButtons={!freeMode && currentStep?.id === 'first_run.step_04_06_continue_or_more' ? (
                  <button
                    style={{ padding: '10px 20px', borderRadius: 8, background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14, boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)' }}
                    onClick={() => setFreeMode(true)}
                    aria-label="Adicionar membro extra"
                    aria-describedby="tutorial-bubble"
                  >
                    + Membro
                  </button>
                ) : null}
              />
            </>
          ))}
        </>
      )}
      {/* Mini tutoriais avulsos (fora do runstep) */}
      <MiniTips2 tutorialActive={runtime.active} />
    </TutorialContext.Provider>
  )
}

export default TutorialProvider
